/**
 * The guide's reading-time rules and text helpers (pure; guide.ts re-exports them). Every pacing constant the queue
 * and the overlay use lives in GuideTiming (M45, Kevin 2026-10-16: "it doesn't stay in the screen for long enough
 * before another tip shows").
 */
import type { GuideKind } from "./guideTypes";
import { SpeechPaces, speechTimeline } from "./bram/bramSpeech";

export const GuideTiming = {
  /** Reading speed on a phone, generous: about 180 words a minute. */
  WordMs: 330,
  /** Time to find the bubble and start reading. */
  BaseMs: 1000,
  /** No line is timed shorter than this. */
  MinReadMs: 3000,
  /** A tip stays this many times its reading time… */
  TipHoldFactor: 1.6,
  /** …and never less than this. */
  MinTipMs: 6000,
  /** A tap this soon after a line appears does not dismiss it (a tap already on its way). */
  TapGuardMs: 500,
  /** "Tap to continue ▸" appears after this, or after the reading time if that is shorter. */
  ContinueCueMs: 1800,
  /** After the screen goes quiet (a banner hides), wait this long before a queued step starts. */
  QuietGraceMs: 400,
  /**
   * The cool-down between one step ending and the next starting. At 0.25 s two tips docked in the same place read as
   * one card blinking (M45): the gap now lets the first leave before the next arrives.
   */
  StepGapMs: 600,
  /**
   * M45 minimum dwell: a line stays on screen at least dwellMs(text) = max(MinDwellMs, words × DwellWordMs + the
   * typewriter's time) before anything but an urgent warning may take its place, and a tip covered by a sheet before
   * then comes back afterwards instead of counting as read.
   */
  MinDwellMs: 3000,
  DwellWordMs: 250,
  /** Something must cover a line's target (or screen) this long before the line steps aside (one-frame hits ignored). */
  CoverSettleMs: 150,
  /** A non-walkthrough line withdrawn unread (its screen went) is not shown again for this long (no instant re-show). */
  ReshowAfterMs: 30000,
  /** A `hard` block's nudge when the player taps outside the hole. */
  NudgeMs: 450,
} as const;

/** The text without its `**` marks. */
export function plainText(text: string): string { return text.replace(/\*\*/g, ""); }
export function wordCount(text: string): number { const t = plainText(text).trim(); return t ? t.split(/\s+/).length : 0; }
/** How long a line takes to read, in ms. */
export function readMs(text: string): number { return Math.max(GuideTiming.MinReadMs, GuideTiming.BaseMs + wordCount(text) * GuideTiming.WordMs); }
/** How long the captain takes to type the line out (bram/bramSpeech.ts paces it by kind). */
export function typeMs(text: string, kind: GuideKind = "tip"): number {
  const plain = plainText(text);
  return plain.length === 0 ? 0 : Math.round(speechTimeline(plain, SpeechPaces[kind]).endMs);
}
/** The least a line stays up before it counts as read or may give way (anything but an urgent warning waits for it). */
export function dwellMs(text: string, kind: GuideKind = "tip"): number {
  return Math.max(GuideTiming.MinDwellMs, wordCount(text) * GuideTiming.DwellWordMs + typeMs(text, kind));
}
/** How long a tip stays (counted only while it is visible and the page and game are not stalled); never under its dwell. */
export function tipHoldMs(text: string): number { return Math.max(GuideTiming.MinTipMs, Math.round(readMs(text) * GuideTiming.TipHoldFactor), dwellMs(text, "tip")); }

/** `**x**` segments, for the overlay to render as text nodes with <b> around the marked ones. */
export function markSegments(text: string): { readonly text: string; readonly bold: boolean }[] {
  return text.split("**").map((t, i) => ({ text: t, bold: i % 2 === 1 })).filter((s) => s.text.length > 0);
}
